import styles from "components/sections/search/store/StoreSearchResult.module.scss";
import {useEffect, useRef, useState} from "react";
import {InfiniteScrollGrid} from "components/list/InfiniteScrollGrid";
import {Axios} from "api";
import {productScrapContextRef} from "context/ProductScrapContext";
import useItemFetcher from "hooks/useItemFetcher";
import ProductListCard from "components/cards/ProductListCard";
import RecommendedQueries from "components/sections/search/RecommendedQueries";
import {KEY_STORE} from "components/sections/search/SearchResult";
import {NoStoreSearchResult} from "components/sections/search/NoSearchResult";
import {SearchSectionComponentId} from "components/sections/SearchSection";
import LoadingSearchResult, {SearchResultError} from "components/sections/search/LoadingSearchResult";
import {captureException, captureMessage} from "@sentry/nextjs";
import {useRouter} from "next/router";
import {
    STORE_SEARCH_FILTER_TYPE_NAME_ONLY,
    STORE_SEARCH_FILTER_TYPE_SPECIAL_PRICE,
    StoreSearchFilter
} from "components/sections/search/store/StoreSearchFilter";
import {StoreSearchSortOrder} from "components/sections/search/store/StoreSearchSortOrder";
import {numberWithComma} from "common/utils";
import useTranslation from "next-translate/useTranslation";


const CHUNK_SIZE = 20;

const StoreSearchResultSecond = (props) => {
    const {setQuery, recommendedQueries, onItemClick} = props;
    const router = useRouter();

    const [error, setError] = useState(false);

    const [searchResultCount, setSearchResultCount] = useState(0);
    const {t} = useTranslation('sections-search-store-StoreSearchResult');

    useEffect(() => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'ViewStoreSearchResultSecondPhase',
        );
    }, []);

    const getQueryFilterInfo = (queryInfo) => {
        const query = queryInfo?.q || '';
        const sortOrder = queryInfo.order;
        const isNameOnly = queryInfo[STORE_SEARCH_FILTER_TYPE_NAME_ONLY] === 'true';
        const isSpecialPriceOnly = queryInfo[STORE_SEARCH_FILTER_TYPE_SPECIAL_PRICE] === 'true';
        const categoryIds = queryInfo.categories ? queryInfo.categories.split(',').map(x => parseInt(x) || null).filter(x => x !== null) : [];
        const brandIds = queryInfo.brands ? queryInfo.brands.split(',').map(x => parseInt(x)).filter(x => x !== null) : [];
        return {
            query,
            sortOrder,
            isNameOnly,
            isSpecialPriceOnly,
            categoryIds,
            brandIds
        };
    };

    const onProductClick = (product, itemIdx) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'StoreSearchResultProductCardClick',
            {
                productId: product.id,
                productName: product.name,
                itemIdx: itemIdx,
            }
        );
        if (onItemClick) {
            onItemClick();
        }
    };

    useEffect(() => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'ViewStoreSearchResult',
        );
    }, []);

    const getItemComponent = (items) => {
        const filteredItems = items.filter(x => Boolean(x));
        const {query, isNameOnly} = getQueryFilterInfo(router.query);
        return (
            <div className={styles.itemListContainer}>
                {
                    filteredItems.map((item, idx) => {
                        return <div className={styles.cardWrapper}>
                            <ProductListCard
                                product={item}
                                onClick={() => onProductClick(item, item.index)}
                                showPromotionDaysLeft
                                highlightKeyword={isNameOnly ? query : null}
                                textPaddingType="medium"
                            />
                        </div>
                    })
                }
                {
                    filteredItems.length === 1 && <div className={styles.cardWrapper}/>
                }
            </div>
        );
    };

    const fetchProducts = async (cursor, size) => {
        try {
            setError(false);
            const {query, sortOrder, isNameOnly, isSpecialPriceOnly, categoryIds, brandIds} = getQueryFilterInfo(router.query);

            const res = await Axios.post(
                `v1/search/store`,
                {
                    cursor,
                    size,
                    query,
                    sort_order: sortOrder,
                    is_name_only: isNameOnly,
                    is_special_price_only: isSpecialPriceOnly,
                    tag_ids: categoryIds,
                    brand_ids: brandIds,
                }
            );
            if (res.status < 400) {
                console.log('fetchProducts res', res);
                if (cursor == null) {
                    setSearchResultCount(res.data.count);
                }
                if (res.data.scrapped_product_ids) {
                    productScrapContextRef.current.addProductIds(res.data.scrapped_product_ids);
                }
                return {
                    nextItems: res.data.products,
                    nextCursor: res.data.cursor,
                }
            } else {
                captureMessage(JSON.stringify(res.data));
                setError(true);
            }
        } catch (e) {
            console.log('fetchProducts error', e);
            setError(true);
            captureException(e);
            return {
                nextItems: null,
                nextCursor: null,
            };
        }
    };

    const {itemFetchInfoState, loadMoreRows, reset} = useItemFetcher(fetchProducts, 2);

    useEffect(() => {
        reset();
        console.log('reset');
        const {query, sortOrder, isNameOnly, isSpecialPriceOnly, categoryIds, brandIds} = getQueryFilterInfo(router.query);
        if (Boolean(query.trim()) || categoryIds.length > 0 || brandIds.length > 0) {
            loadMoreRows(0, CHUNK_SIZE);
            typeof mixpanel !== 'undefined' && mixpanel.track(
                'StoreSearchResultView',
                {query, sortOrder, categoryIds, brandIds, isNameOnly, isSpecialPriceOnly}
            );
        }
    }, [router.query]);

    const renderPlaceholder = (index) => {
        return <div></div>;
    };

    const handleRefresh = () => {
        reset();
        loadMoreRows(0, CHUNK_SIZE);
    };

    const isSearchRequested = Boolean(router.query.q) || Boolean(router.query.categories) || Boolean(router.query.brands);

    return (
        <>
            <StoreSearchFilter setError={setError} queryFilterInfo={getQueryFilterInfo(router.query)}/>
            {
                isSearchRequested &&
                <div className={styles.countContainer}>
                    <span className={styles.countText}>{t('totalCount', {totalCount: numberWithComma(searchResultCount)})}</span>
                    <StoreSearchSortOrder />
                </div>
            }
            {
                isSearchRequested
                    ? (
                        error
                            ? <SearchResultError handleRefresh={handleRefresh}/>
                            :
                            (
                                itemFetchInfoState.rowCount === undefined
                                    ? <LoadingSearchResult/>
                                    : (
                                        itemFetchInfoState.rowCount > 0
                                            ?
                                            <div className={styles.container}>
                                                <InfiniteScrollGrid
                                                    colCount={1}
                                                    remoteRowCount={itemFetchInfoState.finalRowCount || itemFetchInfoState.rowCount || CHUNK_SIZE}
                                                    expectedRowHeight={336}
                                                    chunkSize={CHUNK_SIZE * 2}
                                                    renderRow={getItemComponent}
                                                    fetchData={loadMoreRows}
                                                    dataStorage={itemFetchInfoState?.storage || []}
                                                    renderPlaceholder={renderPlaceholder}
                                                    disabled={false}
                                                    scrollTargetId={SearchSectionComponentId}
                                                />
                                                <div className={styles.bottomPadding}/>
                                            </div>
                                            : <NoStoreSearchResult query={router.query.q}/>
                                    )
                            )
                    )
                    :
                    <div className={styles.container}>
                        <RecommendedQueries category={KEY_STORE} searchQuery={setQuery}
                                            recommendedQueries={recommendedQueries}/>
                    </div>
            }
        </>
    );

}

export default StoreSearchResultSecond;
